if (!customElements.get('product-info-modal')) {
  customElements.define(
    'product-info-modal',
    class ProductInfoModal extends HTMLElement {
      static instance = null;

      constructor() {
        super();

        if (ProductInfoModal.instance) {
          // console.log('ProductInfoModal: Reusing existing instance');
          // Don't initialize anything - we'll proxy to the existing instance
          return;
        }

        //        console.log('ProductInfoModal: Creating first instance');
        ProductInfoModal.instance = this;
        this.debug = false;
        this.initialized = false;
        this.activeUrl = null;
        this.prefetchedContent = new Map();
        this.prefetchInProgress = new Set();
        this.mouseoverListeners = new Map();
      }

      /**
       * This method is called when the custom element is added to the DOM.
       * It initializes necessary settings for the modal, such as enabling prefetching
       * for product pages and setting up video observers.
       */
      connectedCallback() {
        // If this isn't the singleton instance, proxy to it
        if (this !== ProductInfoModal.instance) {
          // console.log('ProductInfoModal: Proxying to singleton instance');
          return;
        }

        // console.log('ProductInfoModal: connectedCallback on singleton');
        if (this.initialized) {
          if (this.debug) console.log('ProductInfoModal: Already initialized, skipping initialization');
          return;
        }
        this.initialized = true;

        document.addEventListener('DOMContentLoaded', () => {
          this.setupPrefetching();
          this.attachInfoModalListeners();
          this.createVideoObserver();
        });

        window.addEventListener('load', () => {
          this.setupPrefetchObservers();
        });
      }

      /**
       * This method is called when the custom element is removed from the DOM.
       * It ensures proper cleanup is performed by disconnecting video observation
       * and removing event listeners.
       */
      disconnectedCallback() {
        this.disconnectVideos();

        // Cleanup all quicklink listeners
        if (this.quicklinkCleanups && this.quicklinkCleanups.length) {
          this.quicklinkCleanups.forEach((cleanup) => cleanup());
          this.quicklinkCleanups = [];
        }

        // Clean up mouseover listeners
        this.removeAllMouseoverListeners();
      }

      attachInfoModalListeners() {
        if (this.debug) console.log('attachInfoModalListeners');

        document.removeEventListener('click', this.handleDocumentClick);

        this.handleDocumentClick = (e) => {
          const modalTrigger = e.target.closest('[data-modal="#ProductInfoModal"]');

          if (modalTrigger) {
            e.preventDefault();
            this.show(modalTrigger);
          }

          if (e.target === this) {
            this.hide();
          }
        };

        document.addEventListener('click', this.handleDocumentClick);

        const closeBtn = this.querySelector('.close');
        closeBtn.addEventListener('click', () => {
          this.hide();
        });
      }

      setupPrefetchObservers() {
        if (this.debug) console.log('setupPrefetchObservers');

        const isDesktop = window.matchMedia('(min-width: 820px)').matches;

        // Clean up existing listeners first
        this.cleanupPrefetchObservers();
        this.removeAllMouseoverListeners();

        const previewElements = document.querySelectorAll(
          '[data-preview-image-product-id], [data-add-on-image-container], .product-info-carousel__item, .text-card__info'
        );
        if (!previewElements.length) return;

        if (isDesktop) {
          // For desktop: use mouseover prefetching
          this.setupMouseoverPrefetching(previewElements);
        } else {
          // For mobile: use intersection observer prefetching (existing behavior)
          previewElements.forEach((element) => {
            const opener = element.querySelector('[data-modal="#ProductInfoModal"]');
            if (!opener) return;

            const pageUrl = opener.href;
            if (!pageUrl) return;

            this.createPrefetchObserver(element, pageUrl);
          });
        }

        this.mediaQueryList = window.matchMedia('(min-width: 820px)');
        this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
        this.mediaQueryList.addEventListener('change', this.handleMediaQueryChange);
      }

      setupMouseoverPrefetching(elements) {
        if (this.debug) console.log('setupMouseoverPrefetching');

        elements.forEach((element) => {
          const opener = element.querySelector('[data-modal="#ProductInfoModal"]');
          if (!opener) return;

          const pageUrl = opener.href;
          if (!pageUrl) return;

          // Create the mouseover handler
          const handleMouseover = () => {
            if (this.debug) console.log('Mouseover detected, prefetching:', pageUrl);

            // Only prefetch if not already prefetched or in progress
            if (!this.prefetchedContent.has(pageUrl) && !this.prefetchInProgress.has(pageUrl)) {
              this.getContent(pageUrl).then(() => {
                // Remove the listener after successful prefetch
                this.removeMouseoverListener(element, pageUrl);
              });
            } else if (this.prefetchedContent.has(pageUrl)) {
              // Content already prefetched, remove the listener
              this.removeMouseoverListener(element, pageUrl);
            }
          };

          // Store the handler reference so we can remove it later
          this.mouseoverListeners.set(pageUrl, { element, handler: handleMouseover });

          // Attach the event listener
          element.addEventListener('mouseover', handleMouseover, { once: false });
          if (this.debug) console.log('Added mouseover listener for:', pageUrl);
        });
      }

      removeMouseoverListener(element, pageUrl) {
        const listenerData = this.mouseoverListeners.get(pageUrl);
        if (listenerData && listenerData.handler) {
          element.removeEventListener('mouseover', listenerData.handler, { once: false });
          this.mouseoverListeners.delete(pageUrl);
          if (this.debug) console.log('Removed mouseover listener for:', pageUrl);
        }
      }

      removeAllMouseoverListeners() {
        if (this.mouseoverListeners && this.mouseoverListeners.size > 0) {
          this.mouseoverListeners.forEach((data, url) => {
            if (data.element && data.handler) {
              data.element.removeEventListener('mouseover', data.handler);
              if (this.debug) console.log('Removed mouseover listener for:', url);
            }
          });
          this.mouseoverListeners.clear();
        }
      }

      handleMediaQueryChange() {
        if (this.debug) console.log('handleMediaQueryChange');
        this.cleanupPrefetchObservers();
        this.removeAllMouseoverListeners();
        this.setupPrefetchObservers();
      }

      cleanupPrefetchObservers() {
        // Clean up intersection observers
        if (this.prefetchObservers) {
          this.prefetchObservers.forEach((observer) => {
            observer.disconnect();
          });
          this.prefetchObservers = [];
        }
      }

      setupPrefetching() {
        if (this.debug) console.log('setupPrefetching');

        const parentEls = document.querySelectorAll('.quicklink-listener-parent');

        this.quicklinkCleanups = [];

        // Set up individual listeners for each parent element
        parentEls.forEach((parentEl) => {
          const cleanup = quicklink.listen({
            el: parentEl,
            origins: [location.hostname],
            delay: 0,
            timeout: 3000,
            throttle: 3,
            priority: true, // High priority prefetch using fetch() instead of <link rel="prefetch">

            hrefFn: (node) => {
              if (node.matches('[data-modal="#ProductInfoModal"]')) {
                return node.href;
              }
            },

            onError: (error) => {
              console.warn('[Quicklink] Listen error:', error);
            },
          });

          // Store the cleanup function
          this.quicklinkCleanups.push(cleanup);
        });
      }

      show(opener) {
        if (this !== ProductInfoModal.instance) {
          return ProductInfoModal.instance.show(opener);
        }
        this.opener = opener;

        const pageUrl = opener.href;
        if (!pageUrl) {
          console.error('No URL found on opener');
          return;
        }

        this.activeUrl = pageUrl;

        window.pauseAllMedia();
        this.style.display = 'block';

        if (this.debug) console.log('opening modal with url:', pageUrl);

        this.getContent(pageUrl).then((content) => {
          if (this.activeUrl === pageUrl && content) {
            this.renderContent(content);
          }
        });

        setTimeout(() => {
          document.body.style.overflow = 'hidden';
          this.classList.add('modal-visuallyshow');
          this.scrollTop = 0;
        }, 300);

        this.addEventListener('transitionend', () => {
          console.log('transitionend');
          trapFocus(this);
        });

        // init tooltips if applicable
        if (typeof window.initModalTooltips === 'function') window.initModalTooltips();
      }

      hide() {
        window.pauseAllMedia();
        removeTrapFocus(this.opener);
        this.style.display = 'none';
        this.classList.remove('modal-visuallyshow');
        this.classList.remove('modal-show');
        document.body.style.overflow = 'auto';

        this.disconnectVideos();
        this.querySelector('[data-modal-body]').innerHTML = '';
        this.activeUrl = null;
      }

      getContent(pageUrl) {
        if (this.prefetchedContent.has(pageUrl)) {
          if (this.debug) console.log('Returning cached content', pageUrl);
          return Promise.resolve(this.prefetchedContent.get(pageUrl));
        }

        if (this.debug) console.log('Prefetching content for', pageUrl);
        // Mark as in progress
        this.prefetchInProgress.add(pageUrl);

        const startTime = performance.now();

        // Use fetch which will leverage browser's cache if quicklink prefetched it
        return fetch(pageUrl, { cache: 'force-cache' })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch content: ${response.status}`);
            }

            const timeElapsed = performance.now() - startTime;

            // Fast responses (<50ms) are likely from cache
            if (this.debug) {
              if (timeElapsed < 50) {
                console.log(
                  `[Browser Cache Hit] Content served from browser cache for ${pageUrl} (${timeElapsed.toFixed(2)}ms)`
                );
              } else {
                console.log(
                  `[Network Request] Content fetched from network for ${pageUrl} (${timeElapsed.toFixed(2)}ms)`
                );
              }
            }

            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const content = html.querySelector('#MainContent');

            if (!content) {
              throw new Error('Could not find #MainContent in fetched HTML');
            }

            const contentHtml = content.innerHTML;
            this.prefetchedContent.set(pageUrl, contentHtml);

            this.preloadVideos(contentHtml, pageUrl);

            if (this.debug) console.log('Successfully prefetched content for', pageUrl);

            this.prefetchInProgress.delete(pageUrl);

            return contentHtml;
          })
          .catch((error) => {
            console.error(`Error fetching content for ${pageUrl}:`, error);
            this.prefetchInProgress.delete(pageUrl);
            return null;
          });
      }

      renderContent(contentHtml) {
        if (!contentHtml) return;

        const modalBody = this.querySelector('[data-modal-body]');
        if (!modalBody) return;

        modalBody.innerHTML = contentHtml;

        // Initialize lazy loading
        const lazyLoadInstance = new LazyLoad({
          elements_selector: '[data-modal-body] .lazy',
        });

        if (lazyLoadInstance) lazyLoadInstance.loadAll();

        this.observeVideos();
        this.initSwiperSliders();
      }

      preloadVideos(contentHtml, pageUrl) {
        // Create a temporary container to parse the HTML content
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = contentHtml;

        // Find all videos in the content
        const videos = tempContainer.querySelectorAll('video');
        if (!videos.length) {
          if (this.debug) console.log('No videos found in prefetched content for', pageUrl);
          return;
        }

        if (this.debug) console.log(`Found ${videos.length} videos in prefetched content for`, pageUrl);

        videos.forEach((videoEl, index) => {
          const sources = videoEl.querySelectorAll('source');
          const videoSources = [];

          // Collect all source information
          sources.forEach((source) => {
            const src = source.getAttribute('src');
            const media = source.getAttribute('media');
            const type = source.getAttribute('type');

            if (src) videoSources.push({ src, media, type });
          });

          if (videoSources.length > 0) {
            this.preloadVideoWithAllSources(videoSources, index);
          }
        });

        if (this.debug) console.log('Triggered video preloading for', pageUrl);
      }

      preloadVideoWithAllSources(videoSources, videoIndex) {
        if (this.debug) console.log(`Preloading video ${videoIndex} with ${videoSources.length} sources`);

        // Create a single video element for all sources
        const videoEl = document.createElement('video');
        videoEl.style.display = 'none';
        videoEl.preload = 'auto';
        videoEl.muted = true;

        // Add all sources - browser will choose the best one
        videoSources.forEach((sourceData) => {
          const sourceEl = document.createElement('source');
          sourceEl.src = sourceData.src;
          if (sourceData.type) sourceEl.type = sourceData.type;
          if (sourceData.media) sourceEl.media = sourceData.media;
          videoEl.appendChild(sourceEl);
        });

        // Listen for successful loading
        videoEl.addEventListener('loadeddata', () => {
          if (this.debug) {
            // Log which source was actually selected
            const currentSrc = videoEl.currentSrc;
            console.log(`Video ${videoIndex} preloaded successfully. Selected source:`, currentSrc);
          }
        });

        // Listen for errors (if no sources are supported)
        videoEl.addEventListener('error', (e) => {
          console.warn(`Video ${videoIndex} failed to preload:`, e);
        });

        // Append to document temporarily to start loading
        document.body.appendChild(videoEl);

        // Remove after loading or timeout to avoid memory leaks
        const cleanup = () => {
          if (document.body.contains(videoEl)) {
            document.body.removeChild(videoEl);
          }
        };

        // Clean up on successful load or after timeout
        videoEl.addEventListener('loadeddata', cleanup, { once: true });
        setTimeout(cleanup, 5000);

        return videoEl;
      }

      initSwiperSliders() {
        const swiperContainers = this.querySelectorAll('.cat-highlights-carousel-swiper');
        if (!swiperContainers.length) return;

        swiperContainers.forEach((container) => {
          const swiper = new Swiper(container, {
            slidesPerView: 'auto',
            spaceBetween: 30,
            breakpoints: {
              820: {
                slidesPerView: 3,
                spaceBetween: 40,
              },
            },
            navigation: {
              nextEl: 'product-info-modal .cat-highlights-carousel-swiper .swiper-button-next',
              prevEl: 'product-info-modal .cat-highlights-carousel-swiper .swiper-button-prev',
            },
            on: {
              init: function () {
                const carousels = document.querySelectorAll('product-info-modal .cat-highlights-carousel-swiper');
                if (carousels.length > 0) {
                  carousels.forEach((carousel) => {
                    carousel.classList.remove('tw-opacity-0');
                  });
                }
              },
            },
          });

          swiper.init();
        });
      }

      createPrefetchObserver(element, pageUrl) {
        if (!this.prefetchObservers) {
          this.prefetchObservers = [];
        }

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.getContent(pageUrl);
                observer.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.5 }
        );

        observer.observe(element);
        this.prefetchObservers.push(observer);
      }

      createVideoObserver() {
        this.videoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.play();
              } else {
                entry.target.pause();
              }
            });
          },
          {
            root: this,
            threshold: 0.5,
          }
        );
      }

      observeVideos() {
        const videos = this.querySelectorAll('video');
        if (!videos.length) return;

        videos.forEach((video) => {
          this.videoObserver.observe(video);
        });
      }

      disconnectVideos() {
        const videos = this.querySelectorAll('video');
        if (!videos.length) return;

        videos.forEach((video) => {
          this.videoObserver.unobserve(video);
        });
      }
    }
  );
}
